import { useMemo } from "react";

import { type Floor } from "@eisox/api/core-api";
import { cn, Plan, Select } from "@eisox/design-system";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useQuery } from "@tanstack/react-query";

import ExpandedRoom from "~/components/expended-room";
import { FieldContainer } from "~/components/field-container";
import { floorPlanQueries } from "~/features/floor/floor-plan.queries";
import { m } from "~/paraglide/messages";
import type { EnhancedRoom } from "~/types/room.type";

interface RoomPlanPickerProps {
  floors: Floor[];
  rooms: EnhancedRoom[];
  selectedPlanId?: string;
  onSelectedPlanIdChange?: (id: string) => void;
  multiple?: boolean;
  value?: string[];
  onValueChange?: (value: string[]) => void;
  className?: string;
}

const RoomPlanPicker = ({
  floors,
  rooms,
  selectedPlanId: selectedPlanIdProp,
  onSelectedPlanIdChange,
  multiple,
  value: valueProp,
  onValueChange,
  className,
}: RoomPlanPickerProps) => {
  const [selectedPlanId, setSelectedPlanId] = useControllableState({
    prop: selectedPlanIdProp,
    onChange: onSelectedPlanIdChange,
    defaultProp: floors[0]?.id ?? "",
  });
  const [value, setValue] = useControllableState({
    prop: valueProp,
    onChange: onValueChange,
    defaultProp: [],
  });

  const { data: plan } = useQuery({
    ...floorPlanQueries.byId(selectedPlanId),
    enabled: !!selectedPlanId,
  });

  const handleClickOnRoom = (roomId: string) => {
    if (multiple) {
      setValue(prev => (prev.includes(roomId) ? prev.filter(id => id !== roomId) : [...prev, roomId]));
    } else {
      setValue([roomId]);
    }
  };

  const roomsOnPlan = useMemo(() => rooms.filter(r => r.floor.id === selectedPlanId), [rooms, selectedPlanId]);

  return (
    <div className={cn("flex h-full flex-col gap-[10px] text-base", className)}>
      <div className="flex items-end justify-between gap-[10px]">
        <FieldContainer label={m.level()}>
          <Select.Root multiple={false} value={selectedPlanId} onValueChange={setSelectedPlanId}>
            <Select.Trigger className="w-[300px]">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              <Select.Input placeholder="Chercher un plan" />
              <Select.Viewport>
                {floors.map(f => (
                  <Select.Item key={f.id} value={f.id}>
                    {f.name}
                  </Select.Item>
                ))}
                <Select.Empty>{m.no_result()}</Select.Empty>
              </Select.Viewport>
            </Select.Content>
          </Select.Root>
        </FieldContainer>
      </div>
      <Plan.Root>
        <div className="relative flex-1">
          <Plan.Controls className="absolute top-[10px] left-[10px] z-1" />
          <Plan.Wrapper className="h-full! w-full!" plan={plan}>
            {roomsOnPlan.map(room => {
              const selected = value.includes(room.id);
              return (
                <Plan.Item key={room.id} defaultPosition={{ x: room.floor.x, y: room.floor.y }} origin="bottom center">
                  <ExpandedRoom selected={selected} onClick={() => handleClickOnRoom(room.id)}>
                    {room.name}
                  </ExpandedRoom>
                </Plan.Item>
              );
            })}
          </Plan.Wrapper>
        </div>
      </Plan.Root>
    </div>
  );
};

RoomPlanPicker.displayName = "RoomPlanPicker";

export { RoomPlanPicker };
export type { RoomPlanPickerProps };
